/**
 * Le client HTTP, rendu disponible à l'arbre React.
 *
 * Même raison que pour le magasin de session : le client est **construit** par
 * `main.tsx` et par chaque test, jamais exporté comme instance de module. Un
 * client global rendrait impossible d'observer, dans un test, ce qui part
 * réellement sur le réseau — et « aucun appel n'a été émis » ne se prouve qu'en
 * observant le client.
 */

import { createContext, useContext } from 'react';
import type { ReactNode } from 'react';

import type { ApiClient } from './api-client.js';

const ApiClientContext = createContext<ApiClient | null>(null);

/** Rend [client] disponible aux composants de [children]. */
export function ApiClientProvider({
  client,
  children,
}: {
  readonly client: ApiClient;
  readonly children: ReactNode;
}): ReactNode {
  return <ApiClientContext.Provider value={client}>{children}</ApiClientContext.Provider>;
}

/** Le client du contexte. Lève hors du fournisseur. */
export function useApiClient(): ApiClient {
  const client = useContext(ApiClientContext);
  if (client === null) {
    throw new Error(
      "ÉCHEC : aucun client HTTP dans le contexte. L'arbre doit être enveloppé " +
        "par <ApiClientProvider>. Un client construit ici à la volée n'aurait ni " +
        "la racine d'appel ni le point d'extension d'authentification.",
    );
  }
  return client;
}
